import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpenIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SelectInput,
  SkeletonTable,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage } from '@/lib/use-list-query-state';
import {
  RESOURCES_QUERY_KEY,
  RESOURCE_AUDIENCE,
  RESOURCE_FORMAT,
  RESOURCE_STATUS,
  audienceLabel,
  fileSize,
  listResources,
  shortDate,
  type Resource,
} from '@/lib/resources';
import { ResourceFormatIcon } from '@/pages/resource-format-icon';
import { TopicsDrawer } from '@/pages/resource-topics-drawer';

const PAGE_SIZE = 25;

/** The filter above the table. Archived resources sit apart, out of "All". */
const VIEW = { all: 'all', published: 'published', drafts: 'drafts', archived: 'archived' } as const;
type View = (typeof VIEW)[keyof typeof VIEW];

/** The audience filter: any, every client, chosen clients, or one client by id. */
const ANY_AUDIENCE = 'any';

const SORT_FIELDS = {
  resource: 'title',
  topic: 'topicName',
  audience: 'audienceLabel',
  views: 'viewCount',
  downloads: 'downloadCount',
  updated: 'updatedAt',
} as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Resource', sortKey: SORT_FIELDS.resource },
  { label: 'Topic', sortKey: SORT_FIELDS.topic },
  { label: 'Who sees it', sortKey: SORT_FIELDS.audience },
  { label: 'Views', sortKey: SORT_FIELDS.views },
  { label: 'Downloads', sortKey: SORT_FIELDS.downloads },
  { label: 'Updated', sortKey: SORT_FIELDS.updated },
];
/** The count columns, by position. */
const RIGHT_ALIGNED = [3, 4] as const;
/** Most recently changed first: what Roy is working on leads. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.updated, direction: 'desc' };

function inView(resource: Resource, view: View): boolean {
  switch (view) {
    case VIEW.all:
      return resource.status !== RESOURCE_STATUS.archived;
    case VIEW.published:
      return resource.status === RESOURCE_STATUS.published;
    case VIEW.drafts:
      return resource.status === RESOURCE_STATUS.draft;
    case VIEW.archived:
      return resource.status === RESOURCE_STATUS.archived;
    default: {
      const never: never = view;
      return never;
    }
  }
}

function inAudience(resource: Resource, audience: string): boolean {
  if (audience === ANY_AUDIENCE) return true;
  if (audience === RESOURCE_AUDIENCE.allClients || audience === RESOURCE_AUDIENCE.chosenClients) {
    return resource.audience.scope === audience;
  }
  return resource.audience.organizations.some((organization) => organization.id === audience);
}

/**
 * Views as the list counts them: a link has no page of its own, so its opens
 * are its views.
 */
function viewsOf(resource: Resource): number {
  return resource.format === RESOURCE_FORMAT.link ? resource.stats.linkOpens : resource.stats.views;
}

/** The second line under a title: the format line, plus a file's size. */
function detailLine(resource: Resource): string {
  return resource.file ? `${resource.file.kind} · ${fileSize(resource.file.size)}` : resource.formatLine;
}

/** A count cell; a resource with nothing to count shows a dash. */
function countCell(value: number, applies: boolean): string {
  return applies && value > 0 ? value.toLocaleString('en-US') : '-';
}

/**
 * Administration → Resources (Roy's portal list, item 13): every guide,
 * article, file and link in the client portals' Resource center, who sees it,
 * and how often it is read. A row opens the editor.
 *
 * @returns The page.
 */
export function ResourcesPage(): React.ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>(VIEW.all);
  const [audience, setAudience] = useState<string>(ANY_AUDIENCE);
  const [topicsOpen, setTopicsOpen] = useState(false);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { page, setPage } = useUrlPage();

  const resources = useQuery({ queryKey: RESOURCES_QUERY_KEY, queryFn: ({ signal }) => listResources(signal) });

  const all = resources.data ?? [];
  const countIn = (candidate: View): number => all.filter((resource) => inView(resource, candidate)).length;
  const clients = [
    ...new Map(all.flatMap((resource) => resource.audience.organizations).map((organization) => [organization.id, organization])).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const needle = search.trim().toLowerCase();
  const rows = sortRows(
    all
      .filter((resource) => inView(resource, view) && inAudience(resource, audience))
      .filter(
        (resource) =>
          needle === '' ||
          `${resource.title} ${resource.summary} ${resource.topic?.name ?? ''} ${resource.tags.join(' ')} ${audienceLabel(resource)}`
            .toLowerCase()
            .includes(needle),
      )
      .map((resource) => ({
        ...resource,
        topicName: resource.topic?.name ?? '',
        audienceLabel: audienceLabel(resource),
        viewCount: viewsOf(resource),
        downloadCount: resource.stats.downloads,
      })),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        breadcrumbs={[{ label: 'Administration' }, { label: 'Resources' }]}
        title="Resources"
        subtitle="Guides, articles, files and links for the client portals’ Resource center. Share one with every client or with chosen clients."
        action={
          <div className="flex flex-wrap gap-2">
            <Button guide="resources-topics" variant="secondary" onClick={() => setTopicsOpen(true)}>
              Topics
            </Button>
            <Button guide="resources-add" onClick={() => navigate('/resources/new')}>Add resource</Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search resources"
          placeholder="A title, a topic, a tag"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl
          guide="resources-filter"
          label="Which resources to show"
          options={[
            { key: VIEW.all, label: `All (${countIn(VIEW.all)})` },
            { key: VIEW.published, label: `Published (${countIn(VIEW.published)})` },
            { key: VIEW.drafts, label: `Drafts (${countIn(VIEW.drafts)})` },
            { key: VIEW.archived, label: `Archived (${countIn(VIEW.archived)})` },
          ]}
          value={view}
          onChange={(next) => {
            setView(next);
            setPage(1);
          }}
        />
        <SelectInput
          aria-label="Who sees it"
          value={audience}
          onChange={(event) => {
            setAudience(event.target.value);
            setPage(1);
          }}
          className="w-auto min-w-[14rem]"
        >
          <option value={ANY_AUDIENCE}>Any audience</option>
          <option value={RESOURCE_AUDIENCE.allClients}>Every client</option>
          <option value={RESOURCE_AUDIENCE.chosenClients}>Chosen clients</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </SelectInput>
      </div>

      {resources.isError ? (
        <ErrorNotice title="Couldn’t load the resources" message={errorMessage(resources.error)} />
      ) : resources.isPending ? (
        <SkeletonTable rows={6} columns={['w-[38%]', 'w-[16%]', 'w-[16%]', 'w-[8%]', 'w-[10%]', 'w-[10%]']} label="Loading resources…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<BookOpenIcon aria-hidden className="size-6" />}
            title={all.length === 0 ? 'No resources yet' : 'No resources match'}
            message={
              all.length === 0
                ? 'Resources are the guides, exercises and reading clients find in their portal’s Resource center. Add the first one to start the library.'
                : 'Try a different search, or show every resource.'
            }
            {...(all.length === 0 ? { action: <Button onClick={() => navigate('/resources/new')}>Add resource</Button> } : {})}
          />
        </Panel>
      ) : (
        <>
          <DataTable guide="resources-table" headers={HEADERS} rightAlignedHeaders={RIGHT_ALIGNED} {...(sort === undefined ? {} : { sort })} onSort={onSort}>
            {shown.map((resource) => (
              <tr key={resource.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <div className="flex items-center gap-3">
                    <ResourceFormatIcon format={resource.format} />
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/resources/${resource.id}`)}
                        className="text-left font-bold text-portal-ink underline-offset-2 hover:text-brand hover:underline"
                      >
                        {resource.title}
                      </button>
                      <span className="block text-portal-micro text-portal-muted">{detailLine(resource)}</span>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{resource.topicName}</td>
                <td className="p-3 text-portal-small">
                  <span className="flex flex-wrap items-center gap-2">
                    {resource.audience.scope === RESOURCE_AUDIENCE.allClients ? (
                      <span className="text-portal-muted">{resource.audienceLabel}</span>
                    ) : (
                      <b>{resource.audienceLabel}</b>
                    )}
                    {resource.status === RESOURCE_STATUS.draft ? <Pill tone="warning">Draft</Pill> : null}
                    {resource.status === RESOURCE_STATUS.archived ? <Pill tone="neutral">Archived</Pill> : null}
                  </span>
                </td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">{countCell(resource.viewCount, true)}</td>
                <td className="p-3 text-right text-portal-small whitespace-nowrap">
                  {countCell(resource.downloadCount, resource.format === RESOURCE_FORMAT.file)}
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{shortDate(resource.updatedAt)}</td>
              </tr>
            ))}
          </DataTable>
          {totalPages > 1 ? (
            <div className="mt-4">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}

      <TopicsDrawer open={topicsOpen} onClose={() => setTopicsOpen(false)} />
    </section>
  );
}
